/**
 * Command palette (cmdk). Cmd/Ctrl+K opens; Escape closes.
 *
 * Now includes:
 *   - Static admin nav (admin-only)
 *   - The user's assigned agents (jump to chat)
 *   - The user's assigned sheets (jump to sheet view)
 *   - Theme toggles
 */
import type { AgentSummary, FolderSummary, SheetSummary } from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  Bot,
  CalendarDays,
  Folder as FolderIconLucide,
  FolderOpen,
  History,
  type LucideIcon,
  Moon,
  Search,
  Settings,
  Sun,
  Table2,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { apiRequest } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { useThemeStore, type Theme } from '../../stores/theme.store';
import { SheetIcon } from '../admin/SheetIcon';


interface NavCommand {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  adminOnly?: boolean;
}

const NAV_COMMANDS: NavCommand[] = [
  { id: 'nav:agents', label: 'Admin · Agents', icon: Bot, to: '/admin/agents', adminOnly: true },
  { id: 'nav:users', label: 'Admin · Users', icon: Users, to: '/admin/users', adminOnly: true },
  { id: 'nav:sheets-admin', label: 'Admin · Sheets', icon: Table2, to: '/admin/sheets', adminOnly: true },
  { id: 'nav:files-admin', label: 'Admin · Files', icon: FolderOpen, to: '/admin/files', adminOnly: true },
  { id: 'nav:media-calendar-admin', label: 'Admin · Media Calendar', icon: CalendarDays, to: '/admin/media-calendar', adminOnly: true },
  { id: 'nav:media-calendar', label: 'Media Calendar', icon: CalendarDays, to: '/media-calendar' },
  {
    id: 'nav:conversations',
    label: 'Admin · Conversations (audit)',
    icon: History,
    to: '/admin/conversations',
    adminOnly: true,
  },
  { id: 'nav:settings', label: 'Admin · Settings', icon: Settings, to: '/admin/settings', adminOnly: true },
  { id: 'nav:chat', label: 'Chat workspace', icon: Search, to: '/chat' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: Props): JSX.Element | null {
  const { user } = useAuth();
  const navigate = useNavigate();
  const setTheme = useThemeStore((s) => s.setTheme);

  // Only fetch the user's assigned items when the palette is open, to avoid
  // pulling them needlessly on every page load.
  const myAgentsQuery = useQuery({
    queryKey: queryKeys.userAgents.list(user?.id ?? ''),
    queryFn: () => apiRequest<AgentSummary[]>(`/users/${user!.id}/agents`),
    enabled: open && !!user,
    staleTime: 60_000,
  });

  const mySheetsQuery = useQuery({
    queryKey: queryKeys.userSheets.list(user?.id ?? ''),
    queryFn: () => apiRequest<SheetSummary[]>(`/users/${user!.id}/sheets`),
    enabled: open && !!user,
    staleTime: 60_000,
  });

  const myFoldersQuery = useQuery({
    queryKey: queryKeys.userFolders.list(user?.id ?? ''),
    queryFn: () => apiRequest<FolderSummary[]>(`/users/${user!.id}/folders`),
    enabled: open && !!user,
    staleTime: 60_000,
  });

  // Global Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const isAdmin = user?.role === 'admin';
  const navItems = NAV_COMMANDS.filter((c) => !c.adminOnly || isAdmin);
  const myAgents = (myAgentsQuery.data ?? []).filter((a) => a.isActive);
  const mySheets = (mySheetsQuery.data ?? []).filter((s) => s.isActive);
  const myFolders = (myFoldersQuery.data ?? []).filter((f) => f.isActive);

  const go = (to: string): void => {
    onOpenChange(false);
    navigate(to);
  };

  const setThemeAndClose = (t: Theme): void => {
    setTheme(t);
    onOpenChange(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-ink/60 px-4 pt-[15vh] backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <Command
        loop
        className="w-full max-w-lg overflow-hidden rounded-surface border border-rule bg-paper shadow-modal"
      >
        <div className="flex items-center gap-2 border-b border-rule px-3">
          <Search className="size-4 text-ink-muted" />
          <Command.Input
            placeholder="Type a page, agent, sheet, or theme…"
            className="flex-1 bg-transparent py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none"
          />
          <kbd className="rounded-tight border border-rule bg-paper-soft px-1.5 py-0.5 font-mono text-[10px] text-ink-muted">
            esc
          </kbd>
        </div>

        <Command.List className="max-h-80 overflow-y-auto p-1">
          <Command.Empty className="px-3 py-6 text-center text-sm text-ink-muted">
            No matches.
          </Command.Empty>

          {myAgents.length > 0 && (
            <Command.Group
              heading="My agents"
              className="text-[11px] font-medium text-ink-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
            >
              {myAgents.map((a) => (
                <Command.Item
                  key={`agent:${a.id}`}
                  value={`${a.name} ${a.slug}`}
                  onSelect={() => go(`/chat/${a.id}`)}
                  className="flex cursor-pointer items-center gap-2.5 rounded-tight px-2.5 py-2 text-sm text-ink aria-selected:bg-paper-soft"
                >
                  <span
                    className="grid size-5 shrink-0 place-items-center rounded-tight text-[9px] font-bold text-paper"
                    style={{ background: a.accentColor ?? 'rgb(var(--color-text))' }}
                  >
                    {a.name.slice(0, 2).toUpperCase()}
                  </span>
                  {a.name}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {mySheets.length > 0 && (
            <Command.Group
              heading="My sheets"
              className="text-[11px] font-medium text-ink-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
            >
              {mySheets.map((s) => (
                <Command.Item
                  key={`sheet:${s.id}`}
                  value={`${s.name} ${s.description ?? ''}`}
                  onSelect={() => go(`/sheets/${s.id}`)}
                  className="flex cursor-pointer items-center gap-2.5 rounded-tight px-2.5 py-2 text-sm text-ink aria-selected:bg-paper-soft"
                >
                  <span
                    className="grid size-5 shrink-0 place-items-center text-ink-muted"
                    style={s.accentColor ? { color: s.accentColor } : undefined}
                  >
                    <SheetIcon name={s.icon} className="size-3.5" />
                  </span>
                  {s.name}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          {myFolders.length > 0 && (
            <Command.Group
              heading="My files"
              className="text-[11px] font-medium text-ink-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
            >
              {myFolders.map((f) => (
                <Command.Item
                  key={`folder:${f.id}`}
                  value={`${f.name} ${f.description ?? ''}`}
                  onSelect={() => go(`/files/${f.id}`)}
                  className="flex cursor-pointer items-center gap-2.5 rounded-tight px-2.5 py-2 text-sm text-ink aria-selected:bg-paper-soft"
                >
                  <span
                    className="grid size-5 shrink-0 place-items-center text-ink-muted"
                    style={f.accentColor ? { color: f.accentColor } : undefined}
                  >
                    {f.icon ? (
                      <SheetIcon name={f.icon} className="size-3.5" />
                    ) : (
                      <FolderIconLucide className="size-3.5" />
                    )}
                  </span>
                  {f.name}
                </Command.Item>
              ))}
            </Command.Group>
          )}

          <Command.Group
            heading="Navigate"
            className="text-[11px] font-medium text-ink-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
          >
            {navItems.map((c) => (
              <Command.Item
                key={c.id}
                value={c.label}
                onSelect={() => go(c.to)}
                className="flex cursor-pointer items-center gap-2.5 rounded-tight px-2.5 py-2 text-sm text-ink aria-selected:bg-paper-soft"
              >
                <c.icon className="size-4 text-ink-muted" />
                {c.label}
              </Command.Item>
            ))}
          </Command.Group>

          <Command.Group
            heading="Theme"
            className="text-[11px] font-medium text-ink-muted [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
          >
            <Command.Item
              value="theme light"
              onSelect={() => setThemeAndClose('light')}
              className="flex cursor-pointer items-center gap-2.5 rounded-tight px-2.5 py-2 text-sm text-ink aria-selected:bg-paper-soft"
            >
              <Sun className="size-4 text-ink-muted" />
              Light theme
            </Command.Item>
            <Command.Item
              value="theme dark"
              onSelect={() => setThemeAndClose('dark')}
              className="flex cursor-pointer items-center gap-2.5 rounded-tight px-2.5 py-2 text-sm text-ink aria-selected:bg-paper-soft"
            >
              <Moon className="size-4 text-ink-muted" />
              Dark theme
            </Command.Item>
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  );
}
